import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { io, Socket } from 'socket.io-client'
import { skipToken } from '@reduxjs/toolkit/query'

import { getSocket } from '@/core/api/socket'
import { TranscriptionSegment, TranscriptionSpeaker } from '@/core/models/transcription'
import { useGetTranscriptionByConsultationQuery } from '@/core/services/transcription'
import { SocketEvent } from '@/core/types/socket-event'
import { selectAccessToken, selectAuthUser } from '@/redux/reducers/auth.reducer'
import { useAppSelector } from '@/redux/rootReducer'
import { getEnv } from '@/utils/helpers'
import { isDoctor } from '@/utils/type-guards'

import { captureMicrophone, createPcmStreamer, findRemoteAudioStream, PcmStreamer, stopStream } from './audio-capture'
import { LiveTranscriptStatus, SpeakerActivity, mergeSegments, speakersFor } from './transcript'

type LiveSegmentEvent = { consultationId: string; transcriptionId: string; segment: TranscriptionSegment }

/** How long a channel may take from connect to the STT provider being ready before `start` gives up. */
const CHANNEL_START_TIMEOUT_MS = 15_000

type CaptureChannel = {
  speaker: TranscriptionSpeaker
  socket: Socket
  stream: MediaStream
  streamer: PcmStreamer
}

export type UseLiveTranscriptionOptions = {
  consultationId?: string
  language?: string
}

export type UseLiveTranscriptionResult = {
  transcriptionId?: string
  segments: TranscriptionSegment[]
  interim: Partial<Record<TranscriptionSpeaker, string>>
  /** Per speaker: speech detected / turn being transcribed. Empty for providers that stream partial text. */
  activity: Partial<Record<TranscriptionSpeaker, SpeakerActivity>>
  status: LiveTranscriptStatus
  error?: string
  /** True when the remote (other side) track was found and is being transcribed too. */
  remoteCaptured: boolean
  elapsedSeconds: number
  isLoading: boolean
  start: () => Promise<void>
  stop: () => void
}

function liveTranscriptionUrl(): string {
  const apiUrl = getEnv('VITE_API_URL') || 'http://localhost:3000'
  const url = /^(?:[a-z]+:)?\/\//i.test(apiUrl) ? new URL(apiUrl) : new URL(apiUrl, window.location.origin)
  return `${url.protocol}//${url.hostname}:${url.port}/live-transcription`
}

/**
 * Live transcription of a consultation: one `/live-transcription` socket per audio source
 * (local mic = me, remote Chime track = the other side), persisted server-side, and the
 * room broadcast so every participant sees the same transcript.
 */
export function useLiveTranscription({ consultationId, language }: UseLiveTranscriptionOptions): UseLiveTranscriptionResult {
  const accessToken = useAppSelector(selectAccessToken)
  const authUser = useAppSelector(selectAuthUser)
  const role = isDoctor(authUser) ? 'doctor' : 'pds'
  const speakers = useMemo(() => speakersFor(role), [role])

  const { data: transcription, isLoading, refetch } = useGetTranscriptionByConsultationQuery(consultationId ?? skipToken)
  const [liveTranscriptionId, setLiveTranscriptionId] = useState<string | undefined>()

  const [segments, setSegments] = useState<TranscriptionSegment[]>([])
  const [interim, setInterim] = useState<Partial<Record<TranscriptionSpeaker, string>>>({})
  const [activity, setActivity] = useState<Partial<Record<TranscriptionSpeaker, SpeakerActivity>>>({})
  const [status, setStatus] = useState<LiveTranscriptStatus>('idle')
  const [error, setError] = useState<string | undefined>()
  const [remoteCaptured, setRemoteCaptured] = useState(false)
  const [elapsedSeconds, setElapsedSeconds] = useState(0)

  const channelsRef = useRef<CaptureChannel[]>([])
  const startedAtRef = useRef<number | null>(null)

  // Seed from the persisted transcript (also picks up dictaphone / recording segments).
  useEffect(() => {
    if (transcription?.segments) setSegments((current) => mergeSegments([], [...transcription.segments, ...current]))
  }, [transcription?._id, transcription?.segments])

  // Room broadcast — segments captured by anyone in the consultation.
  useEffect(() => {
    if (!consultationId) return
    let disposed = false
    let socket: Socket | undefined
    const listener = (event: LiveSegmentEvent) => {
      if (event.consultationId !== consultationId) return
      setLiveTranscriptionId(event.transcriptionId)
      setSegments((current) => mergeSegments(current, [event.segment]))
    }
    getSocket().then((s) => {
      if (disposed) return
      socket = s
      socket.on(SocketEvent.LIVE_TRANSCRIPTION_SEGMENT, listener)
    })
    return () => {
      disposed = true
      socket?.off(SocketEvent.LIVE_TRANSCRIPTION_SEGMENT, listener)
    }
  }, [consultationId])

  // Elapsed timer while listening.
  useEffect(() => {
    if (status !== 'listening') return
    const interval = setInterval(() => {
      if (startedAtRef.current) setElapsedSeconds(Math.floor((Date.now() - startedAtRef.current) / 1000))
    }, 1000)
    return () => clearInterval(interval)
  }, [status])

  const teardown = useCallback(() => {
    for (const channel of channelsRef.current) {
      channel.streamer.stop()
      stopStream(channel.stream)
      channel.socket.emit('stop')
      channel.socket.disconnect()
    }
    channelsRef.current = []
    setInterim({})
    setActivity({})
    setRemoteCaptured(false)
    startedAtRef.current = null
  }, [])

  const openChannel = useCallback(
    (stream: MediaStream, speaker: TranscriptionSpeaker): Promise<CaptureChannel> =>
      new Promise((resolve, reject) => {
        if (!consultationId || !accessToken) return reject(new Error('Not authenticated'))
        // `forceNew`: never share the app's main Manager (polling-first, app auth) — each
        // capture channel gets its own websocket-only connection.
        const socket = io(liveTranscriptionUrl(), {
          auth: { token: accessToken },
          transports: ['websocket'],
          forceNew: true,
          reconnection: true,
          reconnectionAttempts: 3,
        })
        let channel: CaptureChannel | undefined
        const timeout = setTimeout(() => fail('Live transcription did not start in time'), CHANNEL_START_TIMEOUT_MS)
        // Only fails the pending start; once the channel is up, reconnection handles transient errors.
        const fail = (message: string) => {
          if (channel) return
          clearTimeout(timeout)
          socket.disconnect()
          reject(new Error(message))
        }
        socket.on('connect_error', (err) => fail(err.message))
        socket.on('error', (payload: { message?: string }) => {
          if (!channel) fail(payload?.message ?? 'Live transcription error')
          else setError(payload?.message ?? 'Live transcription error')
        })
        socket.on('interim', (payload: { speaker: TranscriptionSpeaker; text: string }) => {
          setInterim((current) => ({ ...current, [payload.speaker]: payload.text }))
        })
        socket.on('activity', (payload: { speaker: TranscriptionSpeaker; activity: SpeakerActivity }) => {
          setActivity((current) => ({ ...current, [payload.speaker]: payload.activity }))
        })
        socket.on('segment', (payload: { consultationId: string; segment: TranscriptionSegment }) => {
          setSegments((current) => mergeSegments(current, [payload.segment]))
          setInterim((current) => ({ ...current, [payload.segment.speaker ?? 'unknown']: '' }))
        })
        // Also fires after an automatic reconnect: the server opens a fresh STT session.
        socket.on('connect', () => {
          socket.emit('start', { consultationId, speaker, language })
        })
        socket.on('ready', () => {
          // After a reconnect the previous streamer is still pumping audio: replace it, never run two.
          channel?.streamer.stop()
          const streamer = createPcmStreamer(stream, (chunk) => {
            if (socket.connected) socket.emit('chunk', { data: chunk })
          })
          if (channel) {
            channel.streamer = streamer
            return
          }
          clearTimeout(timeout)
          channel = { speaker, socket, stream, streamer }
          resolve(channel)
        })
      }),
    [accessToken, consultationId, language]
  )

  const start = useCallback(async () => {
    if (!consultationId || status === 'listening' || status === 'connecting') return
    setError(undefined)
    setStatus('connecting')
    // Held outside the channels until its socket is ready, so a failed start still releases the mic.
    let mic: MediaStream | undefined
    try {
      mic = await captureMicrophone()
      const channels: CaptureChannel[] = [await openChannel(mic, speakers.local)]
      mic = undefined
      const remote = findRemoteAudioStream()
      if (remote) {
        try {
          channels.push(await openChannel(remote, speakers.remote))
          setRemoteCaptured(true)
        } catch (remoteError) {
          stopStream(remote)
          console.warn('[live-transcription] remote track not transcribed:', (remoteError as Error).message)
        }
      }
      channelsRef.current = channels
      startedAtRef.current = Date.now()
      setElapsedSeconds(0)
      setStatus('listening')
    } catch (startError) {
      if (mic) stopStream(mic)
      teardown()
      setError((startError as Error).message)
      setStatus('error')
    }
  }, [consultationId, openChannel, speakers, status, teardown])

  const stop = useCallback(() => {
    if (!channelsRef.current.length) {
      setStatus('idle')
      return
    }
    setStatus('stopping')
    teardown()
    setStatus('idle')
    // The server created / extended the transcription document; pick up its id and any late sentences.
    if (consultationId) refetch()
  }, [consultationId, refetch, teardown])

  useEffect(() => () => teardown(), [teardown])

  return {
    transcriptionId: transcription?._id ?? liveTranscriptionId,
    segments,
    interim,
    activity,
    status,
    error,
    remoteCaptured,
    elapsedSeconds,
    isLoading,
    start,
    stop,
  }
}
